import React, { useMemo } from 'react';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { Grid } from '~/components';
import { asyncState, useAuth } from '~/hooks';
import { useDashboard } from './context';
import { 
    DateCell, 
    NameCell,
    StatusCell,
    AddressCell,
    CompletedBy,
    LockedBy,
    FirmName
} from '~/components/Grid/Cells/GridCells';
import { sortDates, sortStrings } from '~/util/sorters';
import {DashboardActionMenu} from "~/pages/HomePage/Tryon/DashboardGridCells";

const headerClassName = 'bg-gray-50 py-4 px-2 border-b text-left text-sm';
const props = {
    className: 'py-4 px-2 text-sm border-b'
};

const DashboardGrid = ({ pageSize, currentPage, handlePageChange, totalRows, handlePageSizeChange, usePagination = true }) => {

    const { gridData, firmData, filteredRows  } = useDashboard(); 
    const { user } = useAuth();
 
    const isLoading = useMemo(() => gridData.status == asyncState.PENDING, [gridData.status]);
    const rows = Array.isArray(filteredRows) ? filteredRows : [];
    
    const firms = useMemo(
        () => firmData.status == asyncState.SUCCESS ? firmData.value : [],
        [firmData.status]);

    const columns = [
        {
            title: '',
            key: 'actions',
            canSort: false,
            cell: DashboardActionMenu(user),
            headerClassName,
            props: {
                className: 'py-4 px-0 border-b'
            }
        },
        {
            title: 'ID',
            field: 'id',
            headerClassName,
            props
        },
        {
            title: 'Submitted',
            cell: DateCell('submitted'),
            sorter: (a,b) => sortDates(a.submitted, b.submitted),
            headerClassName,
            props
        },
        {
            title: 'Address',
            cell: AddressCell,
            sorter: (a,b) => {
                return sortStrings(
                    `${a.streetNumber} ${a.streetName} ${a.streetSuffix}`,
                    `${b.streetNumber} ${b.streetName} ${b.streetSuffix}`
                );
            },
            headerClassName,
            props
        },
        {
            title: 'County',
            field: 'county',
            headerClassName,
            props
        },
        {
            title: 'Current Owner',
            cell: NameCell,
            sorter: (a,b) => {
                return sortStrings(
                    `${a.ownerFirstName} ${a.ownerLastName}`,
                    `${b.ownerFirstName} ${b.ownerLastName}`
                );
            },
            headerClassName,
            props
        },
        {
            title: 'Locked By',
            cell: LockedBy,
            sorter: sortByLocked,
            headerClassName,
            props
        },
        {
            title: 'Completed By',
            cell: CompletedBy,
            sorter: sortByCompleter,
            headerClassName,
            props
        },
        {
            title: 'Firm Name',
            cell: FirmName(firms),
            sorter: sortByFirm(firms),
            headerClassName,
            props
        },
        {
            title: 'Status',
            cell: StatusCell,
            sorter: (a,b) => a.statusId - b.statusId,
            width: 196,
            headerClassName,
            props
        }
    ];

    return (
        <div className='w-full flex flex-col items-stretch justify-center my-4'>
            {isLoading ? (
                <AiOutlineLoading3Quarters className='text-primary-50 animate-spin m-auto' size='2rem' />
            ) : (
                <Grid 
                    usePagination={usePagination}
                    data={rows} 
                    columns={columns}
                    maxRows={totalRows}
                    className='w-full rounded-md text-sm'
                    tableClassName='mb-2'
                    page={currentPage} 
                    events={{
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange
                    }}
                    initialState={{
                        defaultPageSize: pageSize
                    }}
                />
            )}
        </div>
    );
};

export default DashboardGrid;

function sortByLocked(a, b) {
    if (a.statusId == 2 && b.statusId != 2)
        return -1;
    if (a.statusId != 2 && b.statusId == 2)
        return 1;

    return sortStrings(
        `${a.modifiedBy.firstName} ${b.modifiedBy.lastName}`,
        `${b.modifiedBy.firstName} ${b.modifiedBy.lastName}`
    );
}

function sortByCompleter(a, b) {
    if ([3,4].includes(a.statusId) && ![3,4].includes(b.statusId))
        return -1;
    if (![3,4].includes(a.statusId) && [3,4].includes(b.statusId))
        return 1;

    return sortStrings(
        `${a.modifiedBy.firstName} ${b.modifiedBy.lastName}`,
        `${b.modifiedBy.firstName} ${b.modifiedBy.lastName}`
    );
}

function sortByFirm(firms = []) {
    return (a, b) => {
        const aFirm = firms.find(f => f.Id == a.firmId);
        const bFirm = firms.find(f => f.Id == b.firmId);

        return sortStrings(aFirm?.Name, bFirm?.Name);
    };
}